import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Users, BookOpen, FileText, CheckSquare, Home, Menu, LogOut, BarChart3 } from "lucide-react";
import { Button } from "../pages/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../pages/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "../pages/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "../pages/ui/dropdown-menu";
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from "../services/api";

export default function AdminLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const { user, logout, loading } = useAuth();

  // Check if user is admin
  useEffect(() => {
    if (!loading && user && user.role !== 'admin') {
      // Not admin, redirect to regular dashboard or login
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  // Handle logout
  const handleLogout = async () => {
    await logout();
    window.location.href = '/signin';
  };

  // Show loading while user data is being fetched
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00088E] mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  // If no user after loading is complete, show error state
  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">Erreur: Utilisateur non trouvé</p>
          <a href="/signin" className="text-blue-600 hover:underline">
            Retourner à la connexion
          </a>
        </div>
      </div>
    );
  }

  // If not admin, don't render
  if (user.role !== 'admin') {
    return null;
  }

  // Admin user data with safe fallbacks
  const AdminData = {
    name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Administrateur",
    email: user?.email || "admin@example.com",
    avatar: user?.photo || null,
    role: user?.role || "admin",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
  };

  const getImageUrl = (imagePath: string | null | undefined): string | undefined => {
    if (!imagePath || imagePath === "/placeholder.svg") {
      return undefined; // Let Avatar component show fallback
    }
    
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    return `${API_BASE_URL}${imagePath}`;
  };

  const getInitials = (firstName: string, lastName: string) => {
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
    return firstInitial + lastInitial || 'AD';
  };

  // Navigation items
  const navItems = [
    { href: "/admin", label: "Tableau de bord", icon: BarChart3 },
    { href: "/admin/users", label: "Utilisateurs", icon: Users },
    { href: "/admin/courses", label: "Formations", icon: BookOpen },
    { href: "/admin/inscriptions", label: "Inscriptions", icon: CheckSquare },
  ];

  const isActive = (path: string) => {
    if (path === "/admin" && pathname === "/admin") {
      return true;
    }
    if (path !== "/admin" && pathname.startsWith(path)) {
      return true;
    }
    return false;
  };

  return (
    <div className="flex min-h-screen flex-col">
      {/* Header spécifique au dashboard admin */}
      <header className="border-b bg-white dark:bg-gray-950 sticky top-0 z-10">
        <div className="container flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64">
                <div className="flex flex-col gap-6 py-6">
                  <Link to="/" className="flex items-center gap-2">
                    <img src="/logo.png" alt="Elille Academy Logo" width={100} height={70} />
                  </Link>

                  <div className="flex flex-col gap-2 py-2">
                    <div className="flex items-center gap-2 mb-4">
                      <Avatar>
                        <AvatarImage
                          src={getImageUrl(AdminData.avatar)}
                          alt={AdminData.name}
                          onError={(e) => (e.currentTarget.style.display = 'none')}
                        />
                        <AvatarFallback>
                          {getInitials(AdminData.firstName, AdminData.lastName)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{AdminData.name}</p>
                        <p className="text-xs text-gray-500">{AdminData.email}</p>
                        <p className="text-xs text-gray-400 capitalize">{AdminData.role}</p>
                      </div>
                    </div>

                    <nav className="flex flex-col gap-1">
                      {navItems.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setOpen(false)}
                          className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all ${
                            isActive(item.href)
                              ? "bg-accent text-accent-foreground font-medium"
                              : "text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800"
                          }`}
                        >
                          <item.icon className="h-4 w-4" />
                          {item.label}
                        </Link>
                      ))}
                    </nav>

                    <div className="mt-6 pt-6 border-t">
                      <Link
                        to="/"
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800 mb-1"
                      >
                        <Home className="h-4 w-4" />
                        Retour au site
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800 w-full text-left"
                      >
                        <LogOut className="h-4 w-4" />
                        Déconnexion
                      </button>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            <Link to="/admin" className="flex items-center gap-2">
              <img src="/logo.png" alt="Elille Academy Logo" width={100} height={70} />
            </Link>
          </div>

          <div className="flex items-center gap-4">
            {/* Profil administrateur */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar>
                    <AvatarImage
                      src={getImageUrl(AdminData.avatar)}
                      alt={AdminData.name}
                      onError={(e) => (e.currentTarget.style.display = 'none')}
                    />
                    <AvatarFallback>
                      {getInitials(AdminData.firstName, AdminData.lastName)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{AdminData.name}</p>
                    <p className="text-xs leading-none text-muted-foreground">{AdminData.email}</p>
                    <p className="text-xs leading-none text-muted-foreground capitalize">
                      {AdminData.role}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <Link to="/">
                    <Home className="mr-2 h-4 w-4" />
                    <span>Retour au site</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Déconnexion</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar de navigation (visible uniquement sur desktop) */}
        <aside className="hidden md:flex w-64 flex-col border-r bg-gray-50 dark:bg-gray-900">
          <div className="flex flex-col gap-2 p-4">
            <div className="py-2">
              <h2 className="text-lg font-serif font-semibold">Administration</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Gérez la plateforme</p>
            </div>
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all ${
                    isActive(item.href)
                      ? "bg-accent text-accent-foreground font-medium"
                      : "text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="mt-auto pt-6">
              <Link
                to="/"
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800 mb-1"
              >
                <Home className="h-4 w-4" />
                Retour au site
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-800 w-full text-left"
              >
                <LogOut className="h-4 w-4" />
                Déconnexion
              </button>
            </div>
          </div>
        </aside>

        {/* Contenu principal */}
        <main className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-auto">{children}</main>
      </div>
    </div>
  );
}